import React, { useState } from 'react';
import { X, Check, ChevronRight } from 'lucide-react';
import '../OnboardingModal.css';

const OnboardingModal = ({ onComplete, onSkip, userId }) => {
  const [step, setStep] = useState(1);
  const [saveError, setSaveError] = useState(null);
  const [formData, setFormData] = useState({
    age_range: '',
    medications: '',
    climate: [], // Multi-select array
    allergies: ''
  });

  const totalSteps = 4;

  const handleNext = () => {
    if (step < totalSteps) {
      setStep(step + 1);
    } else {
      handleComplete();
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleComplete = async () => {
    setSaveError(null);
    try {
      console.log('💾 Saving onboarding data:', formData);
      const { supabase } = await import('../supabase');
      const { error } = await supabase.from('user_profiles').upsert({
        user_id: userId,
        age_range: formData.age_range || null,
        medications: formData.medications || null,
        climate: formData.climate.length > 0 ? formData.climate : null,
        allergies: formData.allergies || null,
        updated_at: new Date().toISOString()
      }, {
        onConflict: 'user_id'  // Specify conflict resolution column
      });
      
      if (error) {
        console.error('❌ Error saving onboarding:', error);
        throw error;
      }
      
      console.log('✅ Onboarding saved successfully');
      onComplete(formData);
    } catch (err) {
      console.error('Error saving onboarding data:', err);
      setSaveError('Could not save your profile. Please try again.');
    }
  };

  const isStepComplete = () => {
    switch(step) {
      case 1: return formData.age_range !== '';
      case 2: return formData.medications !== '';
      case 3: return formData.climate.length > 0; // At least one climate selected
      case 4: return formData.allergies !== '';
      default: return false;
    }
  };

  return (
    <div className="onboarding-modal-overlay">
      <div className="onboarding-modal">
        {/* Header */}
        <div className="onboarding-header">
          <button onClick={onSkip} className="skip-button">
            <X className="h-5 w-5" />
          </button>
          <h2 className="onboarding-title">Quick Profile Setup</h2>
          <p className="onboarding-subtitle">Help us personalise your recommendations</p>
        </div>

        {/* Progress Dots */}
        <div className="progress-dots">
          {[1, 2, 3, 4].map((dot) => (
            <div
              key={dot}
              className={`progress-dot ${step >= dot ? 'active' : ''} ${step > dot ? 'complete' : ''}`}
            >
              {step > dot ? <Check className="h-3 w-3" /> : dot}
            </div>
          ))}
        </div>

        {/* Form Steps */}
        <div className="onboarding-content">
          {step === 1 && (
            <div className="form-step">
              <label className="form-label">What's your age range?</label>
              <select
                value={formData.age_range}
                onChange={(e) => setFormData({ ...formData, age_range: e.target.value })}
                className="form-select"
                autoFocus
              >
                <option value="">Select age range</option>
                <option value="Under 18">Under 18</option>
                <option value="18-24">18-24</option>
                <option value="25-34">25-34</option>
                <option value="35-44">35-44</option>
                <option value="45-54">45-54</option>
                <option value="55+">55+</option>
              </select>
              <p className="form-hint">This helps us recommend age-appropriate products</p>
            </div>
          )}

          {step === 2 && (
            <div className="form-step">
              <label className="form-label">Are you on any medications?</label>
              <input
                type="text"
                value={formData.medications}
                onChange={(e) => setFormData({ ...formData, medications: e.target.value })}
                placeholder="e.g., None, Retinoids, Antibiotics"
                className="form-input"
                autoFocus
              />
              <p className="form-hint">Some medications affect skin sensitivity</p>
            </div>
          )}

          {step === 3 && (
            <div className="form-step">
              <label className="form-label">Select climate</label>
              <p className="form-hint mb-3">You can select multiple (e.g., Dry + Cold)</p>
              <div className="climate-options">
                {['Humid', 'Dry', 'Temperate', 'Cold', 'Tropical'].map((climate) => (
                  <button
                    key={climate}
                    type="button"
                    onClick={() => {
                      const newClimate = formData.climate.includes(climate)
                        ? formData.climate.filter(c => c !== climate)
                        : [...formData.climate, climate];
                      setFormData({ ...formData, climate: newClimate });
                    }}
                    className={`climate-option ${formData.climate.includes(climate) ? 'selected' : ''}`}
                  >
                    {formData.climate.includes(climate) && (
                      <Check className="h-4 w-4 mr-2" />
                    )}
                    {climate}
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="form-step">
              <label className="form-label">Any allergies or sensitivities?</label>
              <input
                type="text"
                value={formData.allergies}
                onChange={(e) => setFormData({ ...formData, allergies: e.target.value })}
                placeholder="e.g., None, Fragrance, Nuts"
                className="form-input"
                autoFocus
              />
              <p className="form-hint">We'll avoid products with these ingredients</p>
            </div>
          )}
        </div>

        {saveError && (
          <p className="text-red-600 text-sm text-center mb-2" role="alert">
            {saveError}
          </p>
        )}
        {/* Footer */}
        <div className="onboarding-footer">
          {step > 1 && (
            <button onClick={handleBack} className="secondary-button">
              Back
            </button>
          )}
          <button
            onClick={handleNext}
            disabled={!isStepComplete()}
            className="primary-button"
          >
            {step === totalSteps ? (saveError ? 'Try again' : 'Complete') : 'Next'}
            {step < totalSteps && <ChevronRight className="h-4 w-4 ml-1" />}
          </button>
        </div>

        <button onClick={onSkip} className="skip-text-button">
          I'll do this later
        </button>
      </div>
    </div>
  );
};

export default OnboardingModal;
